import React from 'react';
import type { ProjectAssociation } from '../../../../utils/invoiceUtils';

const fmt = (n: number, ccy: string): string => {
  if (!n && n !== 0) return '—';
  if (ccy === 'USD') return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${ccy}`;
};

export interface ClientDetailBillingEntityRow {
  project: ProjectAssociation;
  totals: { inv: number; paid: number };
  /** Invoice entity keys (`invoiceEntityKey`) tied to this project — drives ENT filter. */
  entityKeys: string[];
}

/**
 * Horizontal strip of billing-entity mini-cards — toggles invoice `entityFilter`.
 */
export const ClientDetailBillingEntityCards: React.FC<{
  rows: ClientDetailBillingEntityRow[];
  displayCcy: string;
  entityFilter: string[];
  onToggleEntityKeys: (keys: string[]) => void;
}> = ({ rows, displayCcy, entityFilter, onToggleEntityKeys }) => {
  if (rows.length === 0) return null;

  return (
    <div
      className="flex flex-wrap gap-1.5 justify-end sm:flex-nowrap sm:overflow-x-auto sm:pb-0.5 sm:-mr-0.5 max-w-full"
      role="group"
      aria-label="Filter by billing entity"
    >
      {rows.map(({ project, totals, entityKeys }, idx) => {
        const pActive = String(project.status || '').toLowerCase() === 'active';
        const name = project.project_name || project.name || `Project ${idx + 1}`;
        const canFilter = entityKeys.length > 0;
        const allIn =
          canFilter && entityKeys.every((k) => entityFilter.includes(k));
        const someIn =
          canFilter &&
          entityKeys.some((k) => entityFilter.includes(k)) &&
          !allIn;

        const cardBody = (
          <>
            <div
              className={`w-1.5 h-1.5 rounded-full flex-none shrink-0 ${pActive ? 'bg-emerald-400' : 'bg-slate-300'}`}
            />
            <div className="min-w-0 text-left">
              <p
                className="text-[11px] font-semibold text-slate-800 leading-tight truncate"
                title={name}
              >
                {name}
              </p>
              <p className="text-[9px] text-slate-600 mt-0.5 whitespace-nowrap">
                Inv{' '}
                <span className="font-semibold text-slate-700">{fmt(totals.inv, displayCcy)}</span>
                {' · '}
                Paid{' '}
                <span className="font-semibold text-emerald-600">{fmt(totals.paid, displayCcy)}</span>
              </p>
            </div>
          </>
        );

        const shellClass = `flex items-center gap-2 rounded-lg px-2.5 py-1.5 flex-none shadow-sm max-w-[min(14rem,42vw)] sm:max-w-[13rem] transition-colors ${
          allIn
            ? 'bg-blue-50/90 border-2 border-blue-400 ring-1 ring-blue-200/80'
            : someIn
              ? 'bg-amber-50/80 border-2 border-amber-400 ring-1 ring-amber-200/70'
              : 'bg-white border border-slate-200'
        } ${
          canFilter
            ? 'cursor-pointer hover:brightness-[0.98]'
            : 'opacity-60 cursor-default'
        }`;

        if (!canFilter) {
          return (
            <div
              key={`${idx}-${name}`}
              className={shellClass}
              title="No matching invoices for filter"
            >
              {cardBody}
            </div>
          );
        }

        return (
          <button
            key={entityKeys.join('\u0000')}
            type="button"
            className={shellClass}
            aria-pressed={allIn}
            title={
              allIn
                ? 'Click to remove from entity filter'
                : 'Click to add to entity filter (multi-select)'
            }
            onClick={() => onToggleEntityKeys(entityKeys)}
          >
            {cardBody}
          </button>
        );
      })}
    </div>
  );
};
